import clsx from 'clsx'
import { X } from 'lucide-react'
import { Children, type HTMLAttributes, type ReactNode } from 'react'
import { Button } from './Button'

export type ChipVariant = 'neutral' | 'primary' | 'outline' | 'danger' | 'success' | 'warning'

export type ChipSize = 'sm' | 'md'

export interface ChipProps extends HTMLAttributes<HTMLSpanElement> {
  /** Optische Variante; Status in Tabellen weiterhin mit `Badge`. */
  variant?: ChipVariant
  size?: ChipSize
  /** Wenn gesetzt, erscheint ein Schließen-Button (z. B. Empfängerliste). */
  onRemove?: () => void
  /** Zusätzliches Label für den Entfernen-Button (z. B. E-Mail-Adresse). */
  removeLabel?: string
  children: ReactNode
}

const variantClass: Record<ChipVariant, string> = {
  neutral:
    'border border-gray-200 bg-gray-100 text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100',
  primary:
    'border border-primary-200 bg-primary-50 text-primary-900 dark:border-primary-800 dark:bg-primary-950/50 dark:text-primary-500',
  outline:
    'border border-gray-300 bg-white text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100',
  danger:
    'border border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100',
  success:
    'border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
  warning:
    'border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200'
}

const sizeClass: Record<ChipSize, string> = {
  sm: 'min-h-[1.5rem] text-xs',
  md: 'min-h-[1.75rem] text-sm'
}

const labelPad: Record<ChipSize, string> = {
  sm: 'pl-2 pr-0.5 py-0.5',
  md: 'pl-2.5 pr-1 py-0.5'
}

/**
 * Interaktives oder statisches Tag (Empfänger, Filter-Tags, Auswahl).
 * Für Status in Tabellen weiterhin `Badge` verwenden.
 */
export function Chip({
  variant = 'neutral',
  size = 'sm',
  onRemove,
  removeLabel = 'Entfernen',
  className,
  children,
  title,
  ...rest
}: ChipProps) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex max-w-full min-w-0 items-center rounded-full font-medium',
        variantClass[variant],
        sizeClass[size],
        className
      )}
      {...rest}
    >
      <span className={clsx('min-w-0 max-w-full truncate', labelPad[size], !onRemove && 'pr-2')}>{children}</span>
      {onRemove ? (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="mr-0.5 h-6 w-6 shrink-0 rounded-full p-0 text-gray-600 hover:bg-black/5 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-white"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          aria-label={removeLabel}
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </Button>
      ) : null}
    </span>
  )
}

export interface ChipListProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  /** Wenn die Liste leer ist, optionaler Platzhalter (z. B. „Keine Empfänger“). */
  empty?: ReactNode
}

/** Container für mehrere `Chip` mit einheitlichem Abstand (Empfängerzeile, Filter). */
export function ChipList({ className, children, empty, ...rest }: ChipListProps) {
  const items = Children.toArray(children)
  if (items.length === 0 && empty != null) {
    return <div className={clsx('text-xs text-gray-500 dark:text-gray-400', className)}>{empty}</div>
  }
  return (
    <div className={clsx('flex flex-wrap items-center gap-2', className)} {...rest}>
      {children}
    </div>
  )
}
